import React from 'react';

/**
 * MarkdownView — tiny, dependency-free markdown renderer.
 *
 * Supports the subset our policy templates use:
 *   - # / ## / ### headings
 *   - `**bold**` and `*italic*`
 *   - Unordered and ordered lists (single level)
 *   - `> blockquotes`
 *   - `---` horizontal rules
 *   - Auto-linked URLs
 *
 * We intentionally do NOT run `dangerouslySetInnerHTML` — every node is
 * created as a real React element, so template content is safe from XSS.
 */
function renderInline(text, keyBase) {
  // Tokenise inline segments: bold, italic, code, link, plain.
  // Simple regex-based tokeniser — order matters (bold before italic).
  const nodes = [];
  let remaining = text;
  let i = 0;
  const patterns = [
    { re: /\*\*([^*]+)\*\*/,               wrap: (m, k) => <strong key={k}>{m[1]}</strong> },
    { re: /(?<!\*)\*(?!\*)([^*]+)\*(?!\*)/, wrap: (m, k) => <em key={k}>{m[1]}</em> },
    { re: /`([^`]+)`/,                     wrap: (m, k) => <code key={k}>{m[1]}</code> },
    { re: /(https?:\/\/[^\s<]+)/,          wrap: (m, k) => (
      <a key={k} href={m[1]} target="_blank" rel="noopener noreferrer">{m[1]}</a>
    )},
  ];
  while (remaining) {
    let earliest = null;
    let earliestIdx = -1;
    let earliestMatch = null;
    for (const p of patterns) {
      const m = p.re.exec(remaining);
      if (m && (earliestIdx === -1 || m.index < earliestIdx)) {
        earliestIdx = m.index;
        earliest = p;
        earliestMatch = m;
      }
    }
    if (!earliest) {
      nodes.push(remaining);
      break;
    }
    if (earliestIdx > 0) nodes.push(remaining.slice(0, earliestIdx));
    nodes.push(earliest.wrap(earliestMatch, `${keyBase}-i${i++}`));
    remaining = remaining.slice(earliestIdx + earliestMatch[0].length);
  }
  return nodes;
}

export function MarkdownView({ source, testId }) {
  if (!source) {
    return (
      <div className="muted" data-testid={testId} style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.9rem' }}>
        No content yet.
      </div>
    );
  }
  const lines = String(source).split('\n');
  const out = [];
  let listBuf = null; // { kind: 'ul' | 'ol', items: [] }

  const flushList = () => {
    if (!listBuf) return;
    const Tag = listBuf.kind;
    out.push(
      <Tag key={`list-${out.length}`} style={{ margin: '10px 0', paddingLeft: 22 }}>
        {listBuf.items.map((it, idx) => (
          <li key={idx} style={{ margin: '4px 0' }}>{renderInline(it, `li-${idx}`)}</li>
        ))}
      </Tag>
    );
    listBuf = null;
  };

  lines.forEach((raw, idx) => {
    const line = raw.replace(/\s+$/, '');
    if (!line.trim()) { flushList(); return; }

    let m;
    if ((m = /^(#{1,6})\s+(.+)$/.exec(line))) {
      flushList();
      const level = m[1].length;
      const Tag = `h${Math.min(level, 6)}`;
      const sizeMap = { 1: '1.5rem', 2: '1.2rem', 3: '1.05rem', 4: '0.95rem', 5: '0.9rem', 6: '0.85rem' };
      out.push(
        <Tag key={`h-${idx}`} style={{ fontSize: sizeMap[level], marginTop: level <= 2 ? '1.1rem' : '0.75rem', marginBottom: '0.4rem', color: 'inherit' }}>
          {renderInline(m[2], `h${level}-${idx}`)}
        </Tag>
      );
      return;
    }
    if ((m = /^\s*(\d+)\.\s+(.+)$/.exec(line))) {
      if (!listBuf || listBuf.kind !== 'ol') { flushList(); listBuf = { kind: 'ol', items: [] }; }
      listBuf.items.push(m[2]);
      return;
    }
    if ((m = /^\s*[-*]\s+(.+)$/.exec(line))) {
      if (!listBuf || listBuf.kind !== 'ul') { flushList(); listBuf = { kind: 'ul', items: [] }; }
      listBuf.items.push(m[1]);
      return;
    }
    if (/^-{3,}\s*$/.test(line)) {
      flushList();
      out.push(<hr key={`hr-${idx}`} style={{ border: 0, borderTop: '1px dashed hsl(var(--border))', margin: '18px 0' }} />);
      return;
    }
    if ((m = /^>\s?(.+)$/.exec(line))) {
      flushList();
      out.push(
        <blockquote key={`bq-${idx}`} style={{ borderLeft: '3px solid hsl(var(--primary))', background: 'hsl(var(--muted) / 0.5)', padding: '8px 14px', margin: '10px 0', borderRadius: '0 6px 6px 0' }}>
          {renderInline(m[1], `bq-${idx}`)}
        </blockquote>
      );
      return;
    }
    flushList();
    out.push(<p key={`p-${idx}`} style={{ margin: '10px 0', lineHeight: 1.55 }}>{renderInline(line, `p-${idx}`)}</p>);
  });
  flushList();

  return <div data-testid={testId}>{out}</div>;
}

export default MarkdownView;
